import React, { useCallback, useEffect, useRef, useState } from 'react';

import { DateTimePicker } from '@mui/x-date-pickers-pro';

import useDebounced from '~/hooks/useDebounced';

import { Dayjs, parseDayjs } from '~/services/TimeService';

const AUTO_COMMIT_DEBOUNCE_MS = 800;

type Props = {
  value: string;
  onChange: (value: string) => void;
  label?: string | null;
  error?: boolean;
  helperText?: string;
  defaultTimezone?: string;
  showDateOnly?: boolean;
  disablePast?: boolean;
  disableFuture?: boolean;
  format?: string;
  disabled?: boolean;
  maxDate?: Dayjs;
  minDate?: Dayjs;
  timeSteps?: { hours?: number; minutes?: number; seconds?: number };
  required?: boolean;
};

export default function DateTimeWidget({
  defaultTimezone = window.configs.DEFAULT_TIMEZONE,
  value,
  label,
  error,
  helperText,
  onChange,
  showDateOnly,
  disablePast,
  disableFuture,
  format,
  disabled,
  maxDate,
  minDate,
  timeSteps = { hours: 1, minutes: 5 },
  required,
}: Props) {
  const widgetLabel = label ? `${label} (${defaultTimezone})` : defaultTimezone;

  const [draftValue, setDraftValue] = useState<Dayjs | null>(() => parseDayjs(value).tz(defaultTimezone));
  const debouncedDraftValue = useDebounced(draftValue, AUTO_COMMIT_DEBOUNCE_MS);

  const defaultTimezoneRef = useRef(defaultTimezone);
  const lastCommittedRef = useRef(parseDayjs(value).tz(defaultTimezone));

  useEffect(() => {
    setDraftValue(parseDayjs(value).tz(defaultTimezone));
    lastCommittedRef.current = parseDayjs(value).tz(defaultTimezone);
    defaultTimezoneRef.current = defaultTimezone;
  }, [value, defaultTimezone]);

  const commitIfChanged = useCallback(
    (newValue: Dayjs | null) => {
      if (newValue?.isValid() && !newValue.isSame(lastCommittedRef.current)) {
        // Update synchronously so a debounce firing before the parent's `value` prop
        // round-trips doesn't see the just-committed value as changed again.
        lastCommittedRef.current = newValue;
        onChange(newValue.tz(defaultTimezoneRef.current).format());
      }
    },
    [onChange],
  );

  useEffect(() => {
    commitIfChanged(debouncedDraftValue);
  }, [debouncedDraftValue, commitIfChanged]);

  const handleAccept = (newValue: Dayjs | null) => {
    commitIfChanged(newValue);
  };

  const handleBlur = () => {
    commitIfChanged(draftValue);
  };

  return (
    <DateTimePicker
      format={format}
      label={widgetLabel}
      value={draftValue}
      onChange={setDraftValue}
      onAccept={handleAccept}
      disablePast={disablePast}
      disableFuture={disableFuture}
      views={showDateOnly ? ['year', 'month', 'day'] : undefined}
      slotProps={{ textField: { fullWidth: true, required, error, helperText, onBlur: handleBlur } }}
      maxDate={maxDate}
      minDate={minDate}
      disabled={disabled}
      timeSteps={timeSteps}
    />
  );
}
